import { Component } from '@theme/component';

/**
 * The mobile "Filter and sort" pill that floats above the product grid.
 *
 * The pill is pure CSS (see the `{% stylesheet %}` in `blocks/filters.liquid`); this component only
 * takes it out of the way once the footer scrolls into view, so it never covers the footer links or
 * the bottom of the last row of cards.
 *
 * Nothing here runs above the mobile breakpoint: the observer is only wired up while the pill is
 * actually displayed, and re-evaluated when the viewport crosses 750px.
 *
 * @extends {Component}
 */
class FacetsStickyToggle extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  #mobileQuery = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    super.connectedCallback();

    this.#mobileQuery.addEventListener('change', this.#handleBreakpointChange);
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#mobileQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#disconnectObserver();
  }

  #handleBreakpointChange = () => {
    this.#sync();
  };

  #sync() {
    this.#disconnectObserver();
    this.removeAttribute('data-hidden');

    if (!this.#mobileQuery.matches) return;

    const footer = document.querySelector('.shopify-section-group-footer-group, footer');

    if (!footer) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.toggleAttribute('data-hidden', Boolean(entry?.isIntersecting));
      },
      { rootMargin: '0px 0px -8px 0px' }
    );

    this.#observer.observe(footer);
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('facets-sticky-toggle')) {
  customElements.define('facets-sticky-toggle', FacetsStickyToggle);
}
